import { WarningOutlined } from '@ant-design/icons';
import { useOidc } from '@axa-fr/react-oidc';
import { Modal, Row } from 'antd';
import { addMinutes, differenceInMilliseconds } from 'date-fns';
import React, { ReactElement, useEffect, useState } from 'react';
import { IIdleTimer, useIdleTimer } from 'react-idle-timer';

export type IdleTimeoutModalProps = {
  baseUrl: string;
};

export default function IdleTimeoutModal(props: IdleTimeoutModalProps): ReactElement | null {
  const { baseUrl } = props;
  const { logout, isAuthenticated } = useOidc('config_show_access_token');
  const [showModal, setShowModal] = useState<boolean>(false);

  if (!isAuthenticated) return <></>;

  // prompt 60 seconds before timeout
  const promptBeforeIdle = 60000;

  // Testing / Specific Time Limit since access token is limited to worker
  //const [tokenTimeout, setTokenTimeout] = useState<Date>(addMinutes(new Date, 59));
  //const promptTimeoutMilliseconds = 120000;  // 2 minutes

  // Official
  //const [tokenTimeout, setTokenTimeout] = useState<Date>(fromUnixTime(accessTokenPayload.exp));
  const promptTimeoutMilliseconds = differenceInMilliseconds(
    addMinutes(new Date(), 59),
    new Date()
  );

  const [remaining, setRemaining] = useState(0);

  // Message handler
  const onMessage = data => {
    //console.log(data);
    switch (data.action) {
      case 'STAY_CONNECTED':
        resetTime();
        break;

      // More actions
      default:
      // no op
    }
  };

  const onIdle = () => {
    // onIdle will be called after the timeout is reached.
    setRemaining(0);
    logout(`${baseUrl}/connect/logout`);
  };

  const onAction = (event?: Event, idleTimer?: IIdleTimer) => {
    // Function to call on user activity.
    // if the prompt is shown, go ahead and reset timer
  };

  const onActive = (event?: Event, idleTimer?: IIdleTimer) => {
    //console.debug("onActive");
  };

  const onPrompt = () => {
    // onPrompt will be called `promptBeforeIdle` milliseconds before `timeout`.
    // All events are disabled while the prompt is active.
    setRemaining(promptBeforeIdle);
    setShowModal(true);
  };

  const { message, getRemainingTime, isPrompted, activate } = useIdleTimer({
    timeout: promptTimeoutMilliseconds,
    promptBeforeIdle,
    onAction,
    onActive,
    onPrompt,
    onIdle,
    onMessage,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200
  });

  useEffect(() => {
    const interval = setInterval(() => {
      // Only show if prompted
      if (isPrompted()) {
        setRemaining(Math.ceil(getRemainingTime() / 1000));
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime, isPrompted]);

  const resetTime = () => {
    setShowModal(false);
    activate();
  };

  return (
    <Modal
      open={showModal}
      closable={false}
      maskClosable={false}
      okText={'Stay Connected'}
      cancelText={'Log Out'}
      onOk={() => message({ action: 'STAY_CONNECTED' }, true)}
      onCancel={() => {
        logout(`${baseUrl}/connect/logout`);
      }}
    >
      <Row>
        <WarningOutlined style={{ fontSize: '32px', color: '#FF8A33', textAlign: 'center' }} />
        <div>Your session is going to expire in {remaining} seconds</div>
      </Row>
    </Modal>
  );
}
